import React from "react";
import Card from "components/card";
import { mercadoPagoApi, shopifyApi, mercadoLibreApi } from "api/endpoints";

/**
 * Configuración de las integraciones de venta por canal.
 *
 * SOBRE LOS TOKENS: nunca se muestran. El backend no los devuelve, ni
 * siquiera al dueño: solo informa si hay uno cargado y sus últimos 4
 * caracteres, que es lo justo para distinguir cuál está puesto. Por eso el
 * campo siempre arranca vacío y dejarlo así conserva el token existente.
 */

const ESTADO_VACIO = { tipo: null, texto: "" };

/**
 * El cliente HTTP devuelve `{ res, data }` y NO lanza excepción cuando el
 * servidor responde con error. Sin desenvolverlo así, un 400 se veía como un
 * guardado exitoso que no guardaba nada.
 */
function desenvolver({ res, data }) {
  if (!res?.ok || data?.ok === false) {
    throw new Error(data?.error || data?.message || `Error ${res?.status ?? "de red"}`);
  }
  return data;
}

function Aviso({ estado }) {
  if (!estado?.texto) return null;
  const color = estado.tipo === "error"
    ? "bg-red-50 text-red-700 border-red-200"
    : "bg-green-50 text-green-700 border-green-200";
  return (
    <div className={`mt-3 rounded-lg border px-3 py-2 text-sm ${color}`}>
      {estado.texto}
    </div>
  );
}

function Campo({ label, ayuda, children }) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-navy-700 dark:text-white">{label}</span>
      {children}
      {ayuda ? (
        <span className="mt-1 block text-xs text-gray-500 dark:text-gray-400">{ayuda}</span>
      ) : null}
    </label>
  );
}

const INPUT = "mt-1 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm "
  + "text-navy-700 outline-none focus:border-brand-500 dark:!border-white/10 "
  + "dark:!bg-navy-800 dark:text-white";

const BOTON = "rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white "
  + "hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50";

const BOTON_SECUNDARIO = "rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium "
  + "text-navy-700 hover:bg-gray-50 disabled:opacity-50 dark:!border-white/10 dark:text-white";

function EstadoToken({ configurado, ultimos4, extra }) {
  if (!configurado) {
    return <span className="text-sm text-gray-500">Sin credencial cargada</span>;
  }
  return (
    <span className="text-sm text-green-700 dark:text-green-400">
      Credencial cargada (termina en {ultimos4 || "····"}){extra}
    </span>
  );
}

export default function IntegracionesTab({ orgUuid }) {
  const [mp, setMp] = React.useState(null);
  const [shopify, setShopify] = React.useState(null);
  const [ml, setMl] = React.useState(null);
  const [mlClientId, setMlClientId] = React.useState("");
  const [mlSecret, setMlSecret] = React.useState("");
  const [mlEstado, setMlEstado] = React.useState(ESTADO_VACIO);
  const [mlOcupado, setMlOcupado] = React.useState(false);
  const [diagnostico, setDiagnostico] = React.useState(null);
  const [cargando, setCargando] = React.useState(true);

  const [mpToken, setMpToken] = React.useState("");
  const [mpVentana, setMpVentana] = React.useState("");
  const [mpEstado, setMpEstado] = React.useState(ESTADO_VACIO);
  const [mpGuardando, setMpGuardando] = React.useState(false);

  const [shClientId, setShClientId] = React.useState("");
  const [shSecret, setShSecret] = React.useState("");
  const [shDominio, setShDominio] = React.useState("");
  const [shAtributo, setShAtributo] = React.useState("");
  const [shEstado, setShEstado] = React.useState(ESTADO_VACIO);
  const [shGuardando, setShGuardando] = React.useState(false);
  const [sincronizando, setSincronizando] = React.useState(null);
  const [programando, setProgramando] = React.useState(false);
  const [programado, setProgramado] = React.useState(null);

  const cargar = React.useCallback(async () => {
    if (!orgUuid) return;
    setCargando(true);
    try {
      const [respMp, respSh, respMl] = await Promise.all([
        mercadoPagoApi.getSettings(orgUuid).then(desenvolver).catch(() => null),
        shopifyApi.getSettings(orgUuid).then(desenvolver).catch(() => null),
        mercadoLibreApi.getSettings(orgUuid).then(desenvolver).catch(() => null),
      ]);
      setMp(respMp?.item ?? null);
      setShopify(respSh?.item ?? null);
      setMl(respMl?.item ?? null);
      setMlClientId(String(respMl?.item?.clientId ?? ""));
      setMpVentana(String(respMp?.item?.matchDiasVentana ?? ""));
      setShDominio(String(respSh?.item?.shopDomain ?? ""));
      setShClientId(String(respSh?.item?.clientId ?? ""));
      setShAtributo(String(respSh?.item?.atributoPaymentId ?? ""));
    } finally {
      setCargando(false);
    }
  }, [orgUuid]);

  React.useEffect(() => { cargar(); }, [cargar]);

  const cargarDiagnostico = React.useCallback(async () => {
    if (!orgUuid) return;
    try {
      setDiagnostico(desenvolver(await shopifyApi.diagnostico(orgUuid)));
    } catch (error) {
      setDiagnostico({ error: error?.message || "No se pudo obtener el diagnóstico." });
    }
  }, [orgUuid]);

  async function guardarMp(habilitado) {
    setMpGuardando(true);
    setMpEstado(ESTADO_VACIO);
    try {
      const resp = desenvolver(await mercadoPagoApi.saveSettings(orgUuid, {
        accessToken: mpToken.trim() || undefined,
        habilitado,
        matchDiasVentana: mpVentana === "" ? undefined : Number(mpVentana),
      }));
      setMp(resp?.item ?? null);
      // El campo se limpia siempre: el token no vuelve del servidor y dejarlo
      // escrito daría la impresión de que se puede volver a leer.
      setMpToken("");
      setMpEstado({ tipo: "ok", texto: "Configuración guardada." });
    } catch (error) {
      setMpEstado({ tipo: "error", texto: error?.message || "No se pudo guardar." });
    } finally {
      setMpGuardando(false);
    }
  }

  async function guardarShopify(habilitado) {
    setShGuardando(true);
    setShEstado(ESTADO_VACIO);
    try {
      const resp = desenvolver(await shopifyApi.saveSettings(orgUuid, {
        clientId: shClientId.trim() || undefined,
        clientSecret: shSecret.trim() || undefined,
        shopDomain: shDominio.trim() || undefined,
        atributoPaymentId: shAtributo.trim() || undefined,
        habilitado,
      }));
      setShopify(resp?.item ?? null);
      // El secreto se limpia; el Client ID no, que no es secreto y sirve verlo.
      setShSecret("");
      setShEstado({
        tipo: "ok",
        // El backend canjea las credenciales contra Shopify antes de
        // guardarlas, así que llegar acá significa que funcionan de verdad.
        texto: "Credenciales verificadas con Shopify y guardadas.",
      });
    } catch (error) {
      setShEstado({ tipo: "error", texto: error?.message || "No se pudo guardar." });
    } finally {
      setShGuardando(false);
    }
  }

  /**
   * Trae los datos ahora, sin esperar al job programado.
   *
   * El rango por omisión son 30 días: la primera vez no sirve de nada traer
   * solo el día de hoy, y es lo que permite ver si el atributo está bien
   * configurado con pedidos reales.
   */
  async function sincronizar(cual) {
    setSincronizando(cual);
    const setEstado = cual === "mp" ? setMpEstado : setShEstado;
    setEstado(ESTADO_VACIO);
    try {
      const desde = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);

      if (cual === "mp") {
        const r = desenvolver(await mercadoPagoApi.sync({ desde }));
        const c = r?.conciliacion || {};
        setEstado({
          tipo: "ok",
          texto: `${r?.traidos ?? 0} pagos traídos · ${c.conciliados ?? 0} cruzados`
            + ` · ${c.ambiguos ?? 0} dudosos · ${c.sinDocumento ?? 0} sin documento.`,
        });
      } else {
        const r = desenvolver(await shopifyApi.sync({ desde }));
        // Solo alarma si FALTAN ids en pedidos de Mercado Pago. Un pedido
        // pagado por transferencia no tiene id de MP y eso es correcto:
        // contarlo acá haría sonar una alarma por algo que está bien.
        const faltantes = r?.mercadoPagoSinId ?? 0;
        const otros = (r?.sinPaymentId ?? 0) - faltantes;
        setEstado({
          tipo: (faltantes > 0 || r?.vinculoError) ? "error" : "ok",
          texto: `${r?.traidas ?? 0} pedidos traídos · ${r?.conPaymentId ?? 0} con id de pago`
            + (r?.vinculados > 0 ? ` · ${r.vinculados} vinculados a su documento` : "")
            + (r?.vinculoError ? `. NO se pudo vincular: ${r.vinculoError}` : "")
            + (faltantes > 0
              ? ` · ${faltantes} de Mercado Pago SIN id: revisá el nombre del atributo.`
              : ".")
            + (otros > 0
              ? ` ${otros} pagados por otra vía, sin id de Mercado Pago (es correcto).`
              : ""),
        });
        await cargarDiagnostico();
      }
      await cargar();
    } catch (error) {
      setEstado({ tipo: "error", texto: error?.message || "No se pudo sincronizar." });
    } finally {
      setSincronizando(null);
    }
  }

  /** Deja la sincronización andando sola, todos los días. */
  async function programarDiaria() {
    setProgramando(true);
    setProgramado(null);
    try {
      const r = desenvolver(await shopifyApi.programarSync(orgUuid));
      setProgramado(r?.jobs || []);
    } catch (error) {
      setProgramado([{ descripcion: error?.message || "No se pudo programar.", creado: false }]);
    } finally {
      setProgramando(false);
    }
  }

  /**
   * MercadoLibre no deja conectar pegando credenciales: el vendedor tiene que
   * autorizar desde el navegador. Esto devuelve el enlace y lo abre.
   */
  async function conectarMercadoLibre() {
    setMlOcupado(true);
    setMlEstado(ESTADO_VACIO);
    try {
      const r = desenvolver(await mercadoLibreApi.conectar(orgUuid, {
        clientId: mlClientId.trim() || undefined,
        clientSecret: mlSecret.trim() || undefined,
      }));
      setMlSecret("");

      if (r.url) {
        // Camino largo: hay que autorizar en el navegador.
        window.open(r.url, "_blank", "noopener");
        setMlEstado({
          tipo: "ok",
          texto: "Se abrió MercadoLibre en otra pestaña. Autorizá ahí y volvé a cargar esta página.",
        });
      } else {
        // Atajo: el token ya servía y quedó conectada.
        setMlEstado({
          tipo: "ok",
          texto: `Cuenta conectada${r.item?.cuenta ? `: ${r.item.cuenta}` : ""}.`,
        });
        await cargar();
      }
    } catch (error) {
      setMlEstado({ tipo: "error", texto: error?.message || "No se pudo iniciar la conexión." });
    } finally {
      setMlOcupado(false);
    }
  }

  async function sincronizarMercadoLibre() {
    setMlOcupado(true);
    setMlEstado(ESTADO_VACIO);
    try {
      const desde = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);
      const r = desenvolver(await mercadoLibreApi.sync({ desde }));
      // El rango se recorre por tramos y se parte solo cuando hace falta, así
      // que el tope de 1000 de MercadoLibre dejó de ser un problema. Solo
      // queda avisar si un ÚNICO día supera los 1000, que no se puede partir.
      const sinPartir = Number(r?.tramosQueTocaronElTope) || 0;
      setMlEstado({
        // Vincular puede fallar sin que la traída falle: son dos pasos. Si
        // falló, se dice. Antes quedaba en "0 vinculados", indistinguible
        // de "no encontró ningún documento".
        tipo: (sinPartir > 0 || r?.vinculoError) ? "error" : "ok",
        texto: `${r?.traidos ?? 0} pedidos traídos · ${r?.conPaymentId ?? 0} con id de pago`
          + (r?.vinculados > 0 ? ` · ${r.vinculados} vinculados a su documento` : "")
          + (r?.vinculoError ? `. NO se pudo vincular: ${r.vinculoError}` : "")
          + (sinPartir > 0
            ? `. Hay ${sinPartir} día(s) con más de 1000 pedidos: MercadoLibre no entrega más que eso ni partiendo el rango.`
            : "."),
      });
      await cargar();
    } catch (error) {
      setMlEstado({ tipo: "error", texto: error?.message || "No se pudo sincronizar." });
    } finally {
      setMlOcupado(false);
    }
  }

  async function desconectarMercadoLibre() {
    setMlOcupado(true);
    try {
      desenvolver(await mercadoLibreApi.desconectar(orgUuid));
      await cargar();
      setMlEstado({ tipo: "ok", texto: "Cuenta desconectada. Los pedidos ya traídos se conservan." });
    } catch (error) {
      setMlEstado({ tipo: "error", texto: error?.message || "No se pudo desconectar." });
    } finally {
      setMlOcupado(false);
    }
  }

  async function quitarCredencial(cual) {
    const api = cual === "mp" ? mercadoPagoApi : shopifyApi;
    const setEstado = cual === "mp" ? setMpEstado : setShEstado;
    try {
      desenvolver(await api.deleteCredential(orgUuid));
      await cargar();
      setEstado({
        tipo: "ok",
        texto: "Credencial eliminada. Los datos ya sincronizados se conservan.",
      });
    } catch (error) {
      setEstado({ tipo: "error", texto: error?.message || "No se pudo eliminar." });
    }
  }

  if (!orgUuid) {
    return (
      <Card extra="w-full p-6">
        <p className="text-sm text-gray-600">Seleccioná una organización para configurar sus integraciones.</p>
      </Card>
    );
  }

  if (cargando) {
    return (
      <Card extra="w-full p-6">
        <p className="text-sm text-gray-600">Cargando configuración…</p>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      {/* ——— Shopify ——— */}
      <Card extra="w-full p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h4 className="text-lg font-bold text-navy-700 dark:text-white">Shopify</h4>
            <p className="mt-1 max-w-2xl text-sm text-gray-600 dark:text-gray-400">
              Trae los pedidos de la tienda. Sirve de puente: el pedido guarda el id del
              pago de Mercado Pago, y con eso la conciliación es exacta en vez de
              adivinar por monto y fecha. <strong>Solo lee</strong>: no emite documentos
              ni modifica la tienda. El token de Shopify vence cada 24 horas, así que
              se renueva solo con estas credenciales.
            </p>
          </div>
          <span className={`rounded-full px-3 py-1 text-xs font-medium ${
            shopify?.habilitado ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"
          }`}>
            {shopify?.habilitado ? "Activa" : "Inactiva"}
          </span>
        </div>

        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <Campo label="Dominio de la tienda" ayuda="Se acepta pegado de cualquier forma: mitienda, mitienda.myshopify.com o la URL completa.">
            <input
              className={INPUT}
              value={shDominio}
              onChange={(e) => setShDominio(e.target.value)}
              placeholder="mitienda.myshopify.com"
            />
          </Campo>

          <Campo label="Client ID" ayuda="Está en el Dev Dashboard de Shopify, en Settings de la app. No es secreto.">
            <input
              className={INPUT}
              value={shClientId}
              onChange={(e) => setShClientId(e.target.value)}
              placeholder="1234567890abcdef…"
            />
          </Campo>

          <Campo label="Client Secret" ayuda="Se canjea contra Shopify al guardar: si no sirve, te enterás acá mismo. Dejalo vacío para conservar el actual.">
            <input
              className={INPUT}
              type="password"
              autoComplete="new-password"
              value={shSecret}
              onChange={(e) => setShSecret(e.target.value)}
              placeholder={shopify?.credencialesConfiguradas ? "•••••••• (sin cambios)" : "shpss_…"}
            />
          </Campo>

          <Campo
            label="Atributo con el id del pago"
            ayuda="Shopify no tiene un campo estándar para esto: poné el nombre que usa tu checkout. Si no calza, el cruce exacto no funciona."
          >
            <input
              className={INPUT}
              value={shAtributo}
              onChange={(e) => setShAtributo(e.target.value)}
              placeholder="mercadopago_payment_id"
            />
          </Campo>

          <div className="flex items-end md:col-span-2">
            {shopify?.credencialesConfiguradas ? (
              <span className="text-sm text-green-700 dark:text-green-400">
                Credenciales cargadas y verificadas
                {shopify?.tokenExpiraAt ? " · el token se renueva solo cada 24 h" : ""}
              </span>
            ) : (
              <span className="text-sm text-gray-500">Sin credenciales cargadas</span>
            )}
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <button className={BOTON} disabled={shGuardando} onClick={() => guardarShopify(true)}>
            {shGuardando ? "Guardando…" : "Guardar y activar"}
          </button>
          {shopify?.habilitado ? (
            <button className={BOTON_SECUNDARIO} disabled={shGuardando} onClick={() => guardarShopify(false)}>
              Desactivar
            </button>
          ) : null}
          {shopify?.habilitado ? (
            <button
              className={BOTON_SECUNDARIO}
              disabled={sincronizando === "shopify"}
              onClick={() => sincronizar("shopify")}
            >
              {sincronizando === "shopify" ? "Trayendo pedidos…" : "Sincronizar ahora"}
            </button>
          ) : null}
          <button className={BOTON_SECUNDARIO} onClick={cargarDiagnostico}>
            Revisar el atributo
          </button>
          {shopify?.credencialesConfiguradas ? (
            <button className={BOTON_SECUNDARIO} onClick={() => quitarCredencial("shopify")}>
              Quitar credencial
            </button>
          ) : null}
        </div>

        <Aviso estado={shEstado} />

        {shopify?.ultimoSyncError ? (
          <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            <strong>Última sincronización con error:</strong> {shopify.ultimoSyncError}
          </div>
        ) : null}

        {diagnostico ? <Diagnostico datos={diagnostico} /> : null}
      </Card>

      {/* ——— MercadoLibre ——— */}
      <Card extra="w-full p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h4 className="text-lg font-bold text-navy-700 dark:text-white">MercadoLibre</h4>
            <p className="mt-1 max-w-2xl text-sm text-gray-600 dark:text-gray-400">
              Trae los pedidos de tu cuenta. Acá el puente es <strong>nativo</strong>: el
              pedido ya incluye el id del pago de Mercado Pago, así que no hay que
              configurar ningún atributo. <strong>Solo lee</strong>: no publica, no
              responde preguntas y no toca tu cuenta.
            </p>
          </div>
          <span className={`rounded-full px-3 py-1 text-xs font-medium ${
            ml?.conectado ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"
          }`}>
            {ml?.conectado ? "Conectada" : "Sin conectar"}
          </span>
        </div>

        {ml?.conectado ? (
          <p className="mt-3 text-sm text-green-700 dark:text-green-400">
            Cuenta conectada{ml.cuenta ? `: ${ml.cuenta}` : ""}. El permiso se renueva solo.
          </p>
        ) : (
          <div className="mt-4 grid gap-4 md:grid-cols-2">
            <Campo label="Client ID" ayuda="De tu aplicación en el Developer Center de MercadoLibre.">
              <input
                className={INPUT}
                value={mlClientId}
                onChange={(e) => setMlClientId(e.target.value)}
                placeholder="1234567890123456"
              />
            </Campo>
            <Campo
              label="Client Secret"
              ayuda="Se usa para pedir el permiso y renovarlo. Se guarda cifrado y nunca se muestra."
            >
              <input
                className={INPUT}
                type="password"
                autoComplete="new-password"
                value={mlSecret}
                onChange={(e) => setMlSecret(e.target.value)}
                placeholder={ml?.aplicacionConfigurada ? "•••••••• (sin cambios)" : ""}
              />
            </Campo>
            <div className="text-xs text-gray-500 md:col-span-2">
              <p>
                A diferencia de Shopify, MercadoLibre exige que autorices desde el navegador
                con tu cuenta de vendedor. Al apretar el botón se abre su pantalla de
                permisos; cuando vuelvas, la cuenta queda conectada.
              </p>
              {/* La URL de retorno tiene que estar cargada en el Developer
                  Center y coincidir EXACTO: si difiere en una barra,
                  MercadoLibre rechaza la autorización. Mostrarla evita
                  descubrirlo por prueba y error. */}
              {ml?.urlDeRetorno ? (
                <p className="mt-2">
                  Antes de conectar, cargá esta URL de retorno en tu aplicación del
                  Developer Center, tal cual:
                  <code className="mt-1 block break-all rounded bg-gray-100 px-2 py-1 font-mono text-[11px] text-navy-700 dark:bg-navy-900 dark:text-white">
                    {ml.urlDeRetorno}
                  </code>
                </p>
              ) : null}
            </div>
          </div>
        )}

        <div className="mt-4 flex flex-wrap gap-2">
          {ml?.conectado ? (
            <>
              <button className={BOTON} disabled={mlOcupado} onClick={sincronizarMercadoLibre}>
                {mlOcupado ? "Trayendo pedidos…" : "Sincronizar ahora"}
              </button>
              <button className={BOTON_SECUNDARIO} disabled={mlOcupado} onClick={desconectarMercadoLibre}>
                Desconectar cuenta
              </button>
            </>
          ) : (
            <button className={BOTON} disabled={mlOcupado} onClick={conectarMercadoLibre}>
              {mlOcupado ? "Abriendo…" : "Conectar con MercadoLibre"}
            </button>
          )}
        </div>

        <Aviso estado={mlEstado} />

        {ml?.ultimoSyncError ? (
          <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            <strong>Última sincronización con error:</strong> {ml.ultimoSyncError}
          </div>
        ) : null}
      </Card>

      {/* ——— Mercado Pago ——— */}
      <Card extra="w-full p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h4 className="text-lg font-bold text-navy-700 dark:text-white">Mercado Pago</h4>
            <p className="mt-1 max-w-2xl text-sm text-gray-600 dark:text-gray-400">
              Trae los pagos recibidos y los cruza con los documentos emitidos, para saber
              qué venta está cobrada. <strong>Solo lee</strong>: no emite documentos, no
              gasta folios y no mueve dinero.
            </p>
          </div>
          <span className={`rounded-full px-3 py-1 text-xs font-medium ${
            mp?.habilitado ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"
          }`}>
            {mp?.habilitado ? "Activa" : "Inactiva"}
          </span>
        </div>

        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <Campo label="Access Token" ayuda="Dejalo vacío para conservar el actual. Nunca se muestra, ni acá ni por la API.">
            <input
              className={INPUT}
              type="password"
              autoComplete="new-password"
              value={mpToken}
              onChange={(e) => setMpToken(e.target.value)}
              placeholder={mp?.tokenConfigurado ? "•••••••• (sin cambios)" : "APP_USR-…"}
            />
          </Campo>

          <Campo
            label="Días de tolerancia"
            ayuda="Diferencia aceptada entre la fecha del pago y la de emisión cuando hay que cruzar por monto."
          >
            <input
              className={INPUT}
              type="number"
              min="0"
              max="60"
              value={mpVentana}
              onChange={(e) => setMpVentana(e.target.value)}
              placeholder="3"
            />
          </Campo>

          <div className="flex items-end md:col-span-2">
            <EstadoToken
              configurado={mp?.tokenConfigurado}
              ultimos4={mp?.tokenUltimos4}
              extra={mp?.esTokenDePruebas ? " — es un token de PRUEBAS" : ""}
            />
          </div>
        </div>

        {mp?.esTokenDePruebas ? (
          <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            El token cargado es de pruebas (empieza con <code>TEST-</code>). Devuelve los
            pagos del entorno de prueba, que no existen: la conciliación va a decir que no
            hay ningún pago.
          </div>
        ) : null}

        <div className="mt-4 flex flex-wrap gap-2">
          <button className={BOTON} disabled={mpGuardando} onClick={() => guardarMp(true)}>
            {mpGuardando ? "Guardando…" : "Guardar y activar"}
          </button>
          {mp?.habilitado ? (
            <button className={BOTON_SECUNDARIO} disabled={mpGuardando} onClick={() => guardarMp(false)}>
              Desactivar
            </button>
          ) : null}
          {mp?.habilitado ? (
            <button
              className={BOTON_SECUNDARIO}
              disabled={sincronizando === "mp"}
              onClick={() => sincronizar("mp")}
            >
              {sincronizando === "mp" ? "Trayendo pagos…" : "Sincronizar ahora"}
            </button>
          ) : null}
          {mp?.tokenConfigurado ? (
            <button className={BOTON_SECUNDARIO} onClick={() => quitarCredencial("mp")}>
              Quitar credencial
            </button>
          ) : null}
        </div>

        <Aviso estado={mpEstado} />

        {mp?.ultimoSyncError ? (
          <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            <strong>Última sincronización con error:</strong> {mp.ultimoSyncError}
          </div>
        ) : null}
      </Card>
      {/* ——— Sincronización automática ——— */}
      <Card extra="w-full p-6">
        <h4 className="text-lg font-bold text-navy-700 dark:text-white">
          Sincronización automática
        </h4>
        <p className="mt-1 max-w-2xl text-sm text-gray-600 dark:text-gray-400">
          Deja los datos al día sin apretar nada: trae los pedidos de Shopify a las
          03:00, los de MercadoLibre a las 03:30 y los pagos de Mercado Pago a las
          04:00. En ese orden, porque los
          pedidos son los que traen el id del pago y sin ellos el cruce sería por
          monto y fecha. <strong>No agrega un worker nuevo</strong>: lo ejecuta el
          mismo que ya corre las demás tareas programadas.
        </p>

        <div className="mt-4">
          <button className={BOTON} disabled={programando} onClick={programarDiaria}>
            {programando ? "Programando…" : "Programar sincronización diaria"}
          </button>
        </div>

        {Array.isArray(programado) && programado.length > 0 ? (
          <div className="mt-3">
            {/* UN RECHAZO NO PUEDE TENER LA MISMA CARA QUE UN ÉXITO.
                Antes, cuando la base rechazaba las tres inserciones, esto
                mostraba tres viñetas grises y nada más: se leía como
                "programado" cuando no se había programado nada. */}
            <ResumenProgramado jobs={programado} />
            <ul className="mt-2 space-y-1 text-sm">
              {programado.map((job, i) => {
                const estado = estadoDeJob(job);
                return (
                  <li key={job.jobType || i} className={COLOR_JOB[estado]}>
                    {MARCA_JOB[estado]}
                    {job.descripcion}
                    {job.motivo ? ` — ${job.motivo}` : ""}
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null}
      </Card>

    </div>
  );
}

/**
 * En qué quedó cada tarea: creada, ya estaba, o no se pudo.
 *
 * Son tres estados, no dos. "Ya estaba programado" es un resultado bueno —el
 * endpoint es idempotente a propósito— y no puede pintarse como un fallo. Pero
 * tampoco puede pintarse igual que un rechazo de la base, que es lo que
 * pasaba: todo lo no-creado salía gris y se leía como "listo".
 *
 * El backend manda `yaEstaba`. Se acepta además el motivo literal porque son
 * dos repos con dos despliegues: entre uno y otro, el panel nuevo convive un
 * rato con el backend viejo, que todavía no manda la bandera.
 */
export function estadoDeJob(job = {}) {
  if (job.creado) return "creado";
  if (job.yaEstaba || job.motivo === "ya estaba programado") return "ya";
  return "error";
}

const COLOR_JOB = {
  creado: "text-green-700 dark:text-green-400",
  ya: "text-gray-600 dark:text-gray-400",
  error: "text-red-700 dark:text-red-400",
};

const MARCA_JOB = { creado: "✓ ", ya: "• ", error: "✗ " };

/**
 * Una línea que dice en qué quedó el conjunto, antes del detalle.
 *
 * Sin esto hay que leer tres viñetas y deducirlo. Y lo que hay que deducir es
 * justamente lo importante: si quedó programado o no.
 */
function ResumenProgramado({ jobs = [] }) {
  const fallaron = jobs.filter((job) => estadoDeJob(job) === "error").length;
  const andando = jobs.length - fallaron;

  if (fallaron === 0) {
    return (
      <div className="rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800 dark:border-green-900 dark:bg-green-950 dark:text-green-300">
        <strong>Sincronización automática activa.</strong> Las {jobs.length} tareas
        están programadas.
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-900 dark:bg-red-950 dark:text-red-300">
      <strong>
        No quedó programada{fallaron === jobs.length ? "" : " del todo"}.
      </strong>{" "}
      {fallaron} de {jobs.length} {fallaron === 1 ? "tarea" : "tareas"} no se
      {fallaron === 1 ? " pudo" : " pudieron"} crear
      {andando > 0 ? `; ${andando} sí está${andando === 1 ? "" : "n"} andando` : ""}.
      Mirá el detalle: si dice que falta una migración, hay que correrla en la
      base antes de volver a intentar.
    </div>
  );
}

function Diagnostico({ datos }) {
  if (datos.error) {
    return (
      <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
        {datos.error}
      </div>
    );
  }

  const filas = Array.isArray(datos.porPasarela) ? datos.porPasarela : [];
  const sinOrdenes = filas.length === 0;

  // El veredicto se calcula acá con los números, en vez de confiar en el
  // flag del servidor. Un backend sin el umbral lo manda en false ante
  // cualquier faltante, y entonces 6 de 750 aparecía como "son demasiados".
  const totalMp = Number(datos.totalOrdenesMercadoPago) || 0;
  const faltanMp = Number(datos.ordenesMercadoPagoSinPaymentId) || 0;
  // Si el nombre no calzara faltarían casi todos, no unos pocos.
  const nombreParaRevisar = totalMp > 0 && faltanMp / totalMp > 0.1;
  const veredicto = totalMp === 0 ? null : !nombreParaRevisar;

  return (
    <div className="mt-4 rounded-xl border border-gray-200 p-4 dark:!border-white/10">
      <h5 className="text-sm font-bold text-navy-700 dark:text-white">
        ¿Está bien puesto el atributo?
      </h5>

      {sinOrdenes ? (
        <p className="mt-2 text-sm text-gray-600">
          Todavía no hay pedidos sincronizados. Sincronizá primero y volvé a revisar.
        </p>
      ) : (
        <>
          {veredicto === true ? (
            faltanMp > 0 ? (
              // Faltan unos pocos: el nombre está bien, esos pedidos puntuales
              // no traen el atributo. Casi siempre son anteriores a que el
              // checkout empezara a guardarlo.
              <p className="mt-2 text-sm text-green-700">
                El atributo está bien puesto: {totalMp - faltanMp} de
                los {totalMp} pedidos de Mercado Pago traen su id y
                van a cruzar exacto. Los {faltanMp} que faltan
                no lo tienen —suelen ser anteriores a que el checkout lo guardara— y
                van a cruzar por monto y fecha.
              </p>
            ) : (
              <p className="mt-2 text-sm text-green-700">
                Correcto: los {totalMp} pedidos de Mercado Pago traen
                su id de pago. El cruce va a ser exacto.
              </p>
            )
          ) : null}
          {veredicto === false ? (
            <p className="mt-2 text-sm text-red-700">
              Faltan {faltanMp} de {totalMp} pedidos
              de Mercado Pago: son demasiados para ser casos sueltos, así que lo más
              probable es que el nombre del atributo no calce. Mientras tanto la
              conciliación va a cruzar por monto y fecha, y dos ventas iguales el mismo
              día van a quedar ambiguas.
            </p>
          ) : null}
          {veredicto === null ? (
            <p className="mt-2 text-sm text-gray-600">
              No hay pedidos pagados con Mercado Pago, así que no hay nada que verificar.
            </p>
          ) : null}

          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase text-gray-500">
                  <th className="py-1 pr-4">Pasarela</th>
                  <th className="py-1 pr-4">Pedidos</th>
                  <th className="py-1 pr-4">Con id</th>
                  <th className="py-1">Sin id</th>
                </tr>
              </thead>
              <tbody>
                {filas.map((fila) => (
                  <tr key={fila.gateway} className="border-t border-gray-100 dark:!border-white/10">
                    <td className="py-1 pr-4 text-navy-700 dark:text-white">{fila.gateway}</td>
                    <td className="py-1 pr-4">{fila.total}</td>
                    <td className="py-1 pr-4">{fila.conPaymentId}</td>
                    <td className="py-1">{fila.sinPaymentId}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="mt-2 text-xs text-gray-500">
            Que un pedido pagado con otra pasarela no tenga id de Mercado Pago es
            correcto, no un problema de configuración.
          </p>

          <VinculoConDocumentos datos={datos} />
        </>
      )}
    </div>
  );
}

/**
 * Los pedidos que calzan con varios documentos, uno por uno.
 *
 * El cruce NO los vincula: elegir uno al azar y pegárselo a una venta
 * corrompe la contabilidad en silencio, y eso es peor que una fila
 * pendiente. Pero "8 pedidos con 2 documentos" no dice qué son.
 *
 * El veredicto separa lo que hay que mirar de lo que tiene explicación —una
 * nota de crédito, un documento partido, uno anulado— para que el contador
 * no revise ocho casos cuando el problema real son dos. No decide nada: la
 * decisión sigue siendo de una persona en todos.
 */
function ListaDeAmbiguos({ pedidos }) {
  if (!Array.isArray(pedidos) || pedidos.length === 0) return null;

  const aRevisar = pedidos.filter((p) => p.veredicto?.revisar);

  return (
    <div className="mt-3">
      <p className="text-xs font-medium text-gray-600 dark:text-gray-400">
        {aRevisar.length === 0
          ? `Los ${pedidos.length} tienen explicación: ninguno es una venta emitida dos veces.`
          : `De ${pedidos.length}, hay ${aRevisar.length} para mirar:`}
      </p>

      <ul className="mt-2 space-y-2">
        {pedidos.map((pedido) => (
          <li
            key={`${pedido.canal}-${pedido.numeroOrden}`}
            className={`rounded-lg border p-2 ${
              pedido.veredicto?.revisar
                ? "border-amber-300 bg-amber-50 dark:border-amber-500/40 dark:!bg-amber-500/10"
                : "border-gray-200 bg-white dark:border-white/10 dark:!bg-navy-800"
            }`}
          >
            <p className="text-xs font-medium text-navy-700 dark:text-white">
              Pedido {pedido.numeroOrden}
              <span className="ml-1 font-normal text-gray-500 dark:text-gray-400">
                · {pedido.canal}
              </span>
            </p>
            <p className="text-[11px] text-gray-600 dark:text-gray-400">
              {pedido.veredicto?.texto}
            </p>
            <ul className="mt-1 space-y-0.5 font-mono text-[11px] text-gray-600 dark:text-gray-400">
              {pedido.documentos?.map((d) => (
                <li key={d.id}>
                  tipo {d.tipoDte} · folio {d.folio} · {d.estado}
                  {d.montoTotal == null ? "" : ` · $${d.montoTotal.toLocaleString("es-CL")}`}
                  {d.esParte ? " · hoja de un documento partido" : ""}
                  {d.oculto ? " · oculto" : ""}
                  {d.razones?.length > 0 ? ` · ${d.razones.join(" / ")}` : ""}
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * El tercer tramo del puente: que el pedido encuentre su documento.
 *
 * Los otros dos se ven al sincronizar. Este depende de que la integración de
 * emisión guarde el id del pedido, y puede fallar en silencio: la
 * conciliación cae a monto y fecha sin que nada lo delate.
 */
function VinculoConDocumentos({ datos }) {
  const vinculo = datos?.vinculo;
  if (!vinculo || vinculo.pedidos === 0) return null;

  const { pedidos, conDocumento } = vinculo;
  const sinDocumento = pedidos - conDocumento;
  // Los que calzan documento pero todavía no tienen el vínculo escrito.
  const porGuardar = Math.max(0, conDocumento - (Number(vinculo.yaGuardados) || 0));
  const proporcion = conDocumento / pedidos;

  return (
    <div className="mt-5 border-t border-gray-100 pt-4 dark:!border-white/10">
      <h5 className="text-sm font-bold text-navy-700 dark:text-white">
        ¿Los pedidos encuentran su documento?
      </h5>

      {conDocumento === 0 ? (
        <p className="mt-2 text-sm text-red-700">
          Ninguno de los {pedidos} pedidos encuentra su documento tributario. El puente
          está cortado en el último tramo: la emisión no está guardando el número de
          pedido, así que la conciliación va a cruzar por monto y fecha por más que los
          pedidos traigan su id de pago. Mirá abajo qué está guardando hoy.
        </p>
      ) : proporcion < 0.5 ? (
        <p className="mt-2 text-sm text-amber-700">
          Solo {conDocumento} de {pedidos} pedidos encuentran su documento. Los otros{" "}
          {sinDocumento} van a cruzar por monto y fecha. Puede ser normal si hay pedidos
          sin facturar todavía; si no, revisá qué guarda la emisión.
        </p>
      ) : (
        <p className="mt-2 text-sm text-green-700">
          {conDocumento} de {pedidos} pedidos encuentran su documento tributario. El
          puente está completo y esos van a cruzar exacto.
          {sinDocumento > 0
            ? ` Los ${sinDocumento} restantes pueden ser pedidos sin facturar todavía.`
            : ""}
          {/* El empujón solo cuando falta algo por guardar: si ya está todo
              escrito, pedir que aprieten un botón de más es ruido. */}
          {porGuardar > 0 ? (
            <>
              {" "}
              <strong>
                Quedan {porGuardar} por guardar: apretá "Sincronizar ahora"
              </strong>
              , que es lo que hace aparecer el método de pago en la lista de documentos.
            </>
          ) : (
            " Ya están todos guardados: el método de pago se ve en la lista de documentos."
          )}
        </p>
      )}

      {datos.porCanal?.length > 0 ? (
        <div className="mt-3">
          <p className="text-xs font-medium text-gray-600 dark:text-gray-400">
            Por canal (el número junto esconde si uno engancha y el otro no):
          </p>
          <table className="mt-1 w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase text-gray-500">
                <th className="py-1 pr-4">Canal</th>
                <th className="py-1 pr-4">Pedidos</th>
                <th className="py-1 pr-4">Con id de pago</th>
                <th className="py-1 pr-4">Encuentran doc.</th>
                <th className="py-1">Vínculo guardado</th>
              </tr>
            </thead>
            <tbody>
              {datos.porCanal.map((c) => (
                <tr key={c.canal} className="border-t border-gray-100 dark:!border-white/10">
                  <td className="py-1 pr-4 text-navy-700 dark:text-white">{c.canal}</td>
                  <td className="py-1 pr-4">{c.pedidos}</td>
                  <td className="py-1 pr-4">{c.conPaymentId}</td>
                  <td className="py-1 pr-4">{c.conDocumento}</td>
                  <td className="py-1">{c.yaGuardados}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {datos.identificadores?.length > 0 ? (
        <div className="mt-3">
          <p className="text-xs font-medium text-gray-600 dark:text-gray-400">
            Con qué identificador engancha cada pedido:
          </p>
          <ul className="mt-1 space-y-0.5 text-sm text-gray-700 dark:text-gray-300">
            {datos.identificadores.map((i) => (
              <li key={`${i.canal}-${i.identificador}`}>
                <span className="font-medium text-navy-700 dark:text-white">{i.canal}</span>
                {" · "}
                {i.pedidos} por {i.identificador}
              </li>
            ))}
          </ul>
          <p className="mt-1 text-xs text-gray-500">
            MercadoLibre tiene dos identificadores —el pedido y el pack, cuando varios
            van juntos en un envío— y conviven. El cruce prueba los dos; esto dice cuál
            está usando cada documento.
          </p>
        </div>
      ) : null}

      {datos.coincidencias?.distribucion?.length > 0 ? (
        <div className="mt-3 rounded-lg bg-gray-50 p-3 dark:!bg-navy-900">
          <p className="text-xs font-medium text-gray-600 dark:text-gray-400">
            Cuántos documentos calza cada pedido (solo se vincula cuando calza uno):
          </p>
          <ul className="mt-1 space-y-0.5 text-sm text-gray-700 dark:text-gray-300">
            {datos.coincidencias.distribucion.map((d) => (
              <li key={d.documentos}>
                {d.documentos === 0
                  ? `${d.pedidos} pedidos sin documento`
                  : d.documentos === 1
                    ? `${d.pedidos} pedidos con 1 documento — se vinculan`
                    : `${d.pedidos} pedidos con ${d.documentos} documentos — NO se vinculan`}
              </li>
            ))}
          </ul>

          <ListaDeAmbiguos pedidos={datos.coincidencias.ambiguos} />
        </div>
      ) : null}

      {datos.muestra ? (
        <div className="mt-3 rounded-lg bg-gray-50 p-3 dark:!bg-navy-900">
          <p className="text-xs font-medium text-gray-600 dark:text-gray-400">
            Los dos lados, para ver qué no calza:
          </p>
          <div className="mt-2 grid gap-3 md:grid-cols-2">
            <div>
              <p className="text-xs font-bold text-navy-700 dark:text-white">Pedidos de Shopify</p>
              <ul className="mt-1 space-y-0.5 font-mono text-[11px] text-gray-600 dark:text-gray-400">
                {datos.muestra.pedidos.map((p) => (
                  <li key={p.shopifyOrderId}>
                    id {p.shopifyOrderId} · nº {p.numeroOrden ?? "—"} · {p.nombreOrden ?? "—"}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p className="text-xs font-bold text-navy-700 dark:text-white">
                Referencias de los documentos
              </p>
              <ul className="mt-1 space-y-0.5 font-mono text-[11px] text-gray-600 dark:text-gray-400">
                {datos.muestra.documentos.map((d) => (
                  <li key={d.folio}>
                    folio {d.folio} ·{" "}
                    {d.referencias.map((r) => `"${r.reason ?? ""}" → ${r.numeroEnRazon ?? "—"}`).join(" / ")}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      ) : null}

      {/* Esta tabla solo ayuda cuando el enganche falla. Con el puente
          funcionando, "0 con id externo" se lee como un problema y no lo es:
          el vínculo va por las referencias, no por ese campo. */}
      {conDocumento === 0 && Array.isArray(datos.idsExternos) && datos.idsExternos.length > 0 ? (
        <div className="mt-3">
          <p className="text-xs font-medium text-gray-600 dark:text-gray-400">
            Qué guarda la emisión en el id externo (últimos 60 días):
          </p>
          <table className="mt-1 w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase text-gray-500">
                <th className="py-1 pr-4">Sistema origen</th>
                <th className="py-1 pr-4">Documentos</th>
                <th className="py-1 pr-4">Con id externo</th>
                <th className="py-1">Ejemplos</th>
              </tr>
            </thead>
            <tbody>
              {datos.idsExternos.map((fila) => (
                <tr
                  key={fila.sourceSystem}
                  className="border-t border-gray-100 dark:!border-white/10"
                >
                  <td className="py-1 pr-4 text-navy-700 dark:text-white">
                    {fila.sourceSystem}
                  </td>
                  <td className="py-1 pr-4">{fila.documentos}</td>
                  <td className="py-1 pr-4">{fila.conIdExterno}</td>
                  <td className="py-1 font-mono text-xs text-gray-600 dark:text-gray-400">
                    {fila.ejemplos.length > 0 ? fila.ejemplos.join(", ") : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}
